import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, Mail, Lock, User, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface UpgradeAccountNudgeProps {
  /** Compact card mode (dashboard) vs full card */
  variant?: 'card' | 'compact';
}

export const UpgradeAccountNudge = ({ variant = 'card' }: UpgradeAccountNudgeProps) => {
  const { isGuest } = useAuth();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(() => {
    const ts = sessionStorage.getItem('upgradeNudgeDismissed');
    return ts === 'true';
  });
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('guestEmail') || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Listen for online/offline
  useState(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  });

  if (!isGuest || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('upgradeNudgeDismissed', 'true');
  };

  const migrateGuestData = async (userId: string) => {
    // Migrate profile
    const savedProfile = localStorage.getItem('ramadanProfile');
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        await supabase.from('profiles').update({
          name: profile.name || name || null,
          ramadan_start_date: profile.ramadanStartDate || null,
          preferences: profile.preferences || {}
        }).eq('user_id', userId);
      } catch (e) {
        console.error('Profile migration error:', e);
      }
    }

    // Migrate tracker data
    const savedProgress = localStorage.getItem('ramadanRenewalProgress');
    if (savedProgress) {
      try {
        const { trackerData } = JSON.parse(savedProgress);
        if (trackerData) {
          // We just keep it in localStorage for now — the useTrackerData hook
          // will pick it up on next load since user is now authenticated.
          // The hook's saveDayData will sync it properly.
          console.log('Guest tracker data ready for sync');
        }
      } catch (e) {
        console.error('Tracker migration error:', e);
      }
    }

    // Migrate journal entries
    const savedJournal = localStorage.getItem('ramadanJournal');
    if (savedJournal) {
      try {
        const entries = JSON.parse(savedJournal);
        if (Array.isArray(entries)) {
          for (const entry of entries) {
            await supabase.from('daily_logs').insert({
              user_id: userId,
              date: entry.date || new Date().toISOString().split('T')[0],
              mood: entry.mood || null,
              gratitude: entry.gratitude || null,
              reflection: entry.reflection || null,
              needs_from_allah: entry.needsFromAllah || null,
              tags: entry.tags || null,
              title: entry.title || null
            });
          }
        }
      } catch (e) {
        console.error('Journal migration error:', e);
      }
    }
  };

  const handleUpgrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOffline) return;
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { name: name || undefined }
        }
      });

      if (error) {
        toast({
          title: 'Could not create account',
          description: error.message,
          variant: 'destructive'
        });
        return;
      }

      if (data.user) {
        // Migrate guest data to new account
        await migrateGuestData(data.user.id);

        // Clean up guest state
        localStorage.removeItem('isGuest');
        localStorage.removeItem('guestEmail');

        toast({
          title: 'Account created',
          description: 'Your journey is now saved — welcome ✨'
        });

        setShowForm(false);
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      toast({
        title: 'Something went wrong',
        description: 'Please try again. Your progress is safe.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.5 }}
      className={`relative border border-border/60 rounded-lg overflow-hidden ${
      variant === 'compact' ? 'bg-card p-3' : 'bg-card p-5'}`
      }>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        aria-label="Dismiss">

        <X className="w-3.5 h-3.5" />
      </button>

      <AnimatePresence mode="wait">
        {!showForm ?
        <motion.div
          key="nudge"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`flex items-start gap-3 ${variant === 'compact' ? 'pr-6' : 'pr-8'}`}>

            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-display text-foreground ${variant === 'compact' ? 'text-sm' : 'text-base'}`}>
                Save your journey
              </h3>
              <p className={`text-muted-foreground mt-1 leading-relaxed ${variant === 'compact' ? 'text-xs' : 'text-sm'}`}>You’re currently using Layali as a guest. Create a free account to save your duʿās and access your journey on any device.

            </p>
              <div className="flex items-center gap-3 mt-3">
                <Button
                size="sm"
                onClick={() => setShowForm(true)}
                className="text-xs">

                  Create account
                </Button>
                <button
                onClick={handleDismiss}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors">

                  Not now
                </button>
              </div>
            </div>
          </motion.div> :

        <motion.div
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}>

            <h3 className="font-display text-base text-foreground mb-4">Create your account</h3>

            {isOffline &&
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-md p-2.5 mb-3">
                <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Connect to the internet to create your account; your progress is still stored on this device.</span>
              </div>
          }

            <form onSubmit={handleUpgrade} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="upgrade-name" className="text-xs">Name (optional)</Label>
                <div className="relative">
                  <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                  id="upgrade-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="pl-8 h-9 text-sm" />

                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="upgrade-email" className="text-xs">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                  id="upgrade-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-8 h-9 text-sm" />

                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="upgrade-password" className="text-xs">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                  id="upgrade-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  className="pl-8 h-9 text-sm" />

                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button
                type="submit"
                size="sm"
                disabled={isLoading || isOffline}
                className="text-xs">

                  {isLoading ? 'Creating...' : 'Create account'}
                </Button>
                <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors">

                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        }
      </AnimatePresence>
    </motion.div>);

};